import { useMentionMediaQuery } from '@/hooks/queries/use-mention-media-query';
import { useMentionQuery } from '@/hooks/queries/use-mention-query';
import { useMentionNavigatorStore } from '@/stores/mention-navigator';
import type { Mention } from '@fluenceur/types';
import { MentionMediaType } from '@fluenceur/types';
import { parseAsString, useQueryState } from 'nuqs';
import React from 'react';
import { PreviewMentionModal, type PreviewMentionNavigator } from './PreviewMentionModal';

type RenderedPayload = {
  mentionId: string;
  mention: Mention;
  mediaUrl: string | null;
  mediaType: MentionMediaType;
  permalink: string | null | undefined;
  hostedThumbnailUrl: string | null | undefined;
  coverUrl: string | null;
};

const GlobalMentionPreviewModal: React.FC = () => {
  const [mentionId, setMentionId] = useQueryState('previewMention', parseAsString);
  const [mediaRefreshRequest, setMediaRefreshRequest] = React.useState<{
    mentionId: string;
    token: number;
  } | null>(null);

  const { data: mention, error: mentionError } = useMentionQuery({
    mentionId: mentionId || '',
    enabled: !!mentionId,
  });

  const { data: mediaData, error: mediaError } = useMentionMediaQuery({
    mentionId: mentionId || '',
    enabled: !!mentionId,
    refreshToken: mediaRefreshRequest?.mentionId === mentionId ? mediaRefreshRequest.token : 0,
  });

  const contextKey = useMentionNavigatorStore((s) => s.contextKey);
  const mentionIds = useMentionNavigatorStore((s) => s.mentionIds);
  const total = useMentionNavigatorStore((s) => s.total);
  const hasNextPage = useMentionNavigatorStore((s) => s.hasNextPage);
  const isFetchingNextPage = useMentionNavigatorStore((s) => s.isFetchingNextPage);
  const fetchNextPage = useMentionNavigatorStore((s) => s.fetchNextPage);

  const currentIndex = mentionId ? mentionIds.indexOf(mentionId) : -1;
  // Snapshot of contextKey at the moment the user triggered the fetch-and-advance.
  // If the list is swapped out mid-fetch (filter change, drawer close), we refuse to navigate.
  const pendingAdvanceRef = React.useRef<string | null>(null);
  const refreshedMediaMentionsRef = React.useRef<Set<string>>(new Set());
  // Keep the last fully-rendered payload so the Dialog stays mounted while the
  // next mention's queries are in flight — prevents a close/reopen flicker when
  // navigating with prev/next.
  const [lastRendered, setLastRendered] = React.useState<RenderedPayload | null>(null);

  React.useEffect(() => {
    if (mentionError || mediaError) {
      console.warn('Error loading mention data:', mentionError || mediaError);
      setMentionId(null);
    }
  }, [mentionError, mediaError, setMentionId]);

  React.useEffect(() => {
    if (!pendingAdvanceRef.current) return;
    if (pendingAdvanceRef.current !== contextKey) {
      pendingAdvanceRef.current = null;
      return;
    }
    if (currentIndex < 0) return;
    const nextId = mentionIds[currentIndex + 1];
    if (nextId) {
      pendingAdvanceRef.current = null;
      void setMentionId(nextId);
    } else if (!isFetchingNextPage && !hasNextPage) {
      pendingAdvanceRef.current = null;
    }
  }, [contextKey, mentionIds, currentIndex, isFetchingNextPage, hasNextPage, setMentionId]);

  const handlePrev = React.useCallback(() => {
    if (currentIndex <= 0) return;
    const prevId = mentionIds[currentIndex - 1];
    if (prevId) void setMentionId(prevId);
  }, [currentIndex, mentionIds, setMentionId]);

  const handleNext = React.useCallback(() => {
    if (currentIndex < 0) return;
    const nextId = mentionIds[currentIndex + 1];
    if (nextId) {
      void setMentionId(nextId);
      return;
    }
    if (hasNextPage && fetchNextPage && !isFetchingNextPage) {
      pendingAdvanceRef.current = contextKey;
      fetchNextPage();
    }
  }, [
    currentIndex,
    mentionIds,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    setMentionId,
    contextKey,
  ]);

  // Keep handler identities out of the keyboard effect deps to avoid rebinding on every render.
  const handlePrevRef = React.useRef(handlePrev);
  const handleNextRef = React.useRef(handleNext);
  React.useEffect(() => {
    handlePrevRef.current = handlePrev;
    handleNextRef.current = handleNext;
  }, [handlePrev, handleNext]);

  const keyboardActive = !!mentionId;
  React.useEffect(() => {
    if (!keyboardActive) return;
    const handler = (e: KeyboardEvent) => {
      const active = document.activeElement;
      const tag = active?.tagName;
      const isEditable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (isEditable) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevRef.current();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextRef.current();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [keyboardActive]);

  const handleClose = React.useCallback(() => {
    pendingAdvanceRef.current = null;
    refreshedMediaMentionsRef.current.clear();
    setLastRendered(null);
    setMediaRefreshRequest(null);
    setMentionId(null);
  }, [setMentionId]);

  const freshPayload = React.useMemo<RenderedPayload | null>(() => {
    if (!mentionId || !mention) return null;
    if (!mediaData) return null;
    const validMediaType = Object.values(MentionMediaType).includes(
      mediaData.mediaType as MentionMediaType
    )
      ? (mediaData.mediaType as MentionMediaType)
      : MentionMediaType.IMAGE;
    return {
      mentionId,
      mention,
      mediaUrl: mediaData.mediaUrl ?? null,
      mediaType: validMediaType,
      permalink: mediaData.permalink,
      hostedThumbnailUrl: mediaData.hostedThumbnailUrl ?? null,
      coverUrl: mediaData.coverUrl ?? null,
    };
  }, [mentionId, mention, mediaData]);

  React.useEffect(() => {
    if (freshPayload) setLastRendered(freshPayload);
  }, [freshPayload]);

  const payload = freshPayload ?? lastRendered;
  // A cover is a signed address too: when it lapses, the same refresh renews it.
  const mediaUrlForRefresh = payload?.mediaUrl ?? payload?.coverUrl ?? null;
  const handleMediaError = React.useCallback(() => {
    if (!mentionId || !mediaUrlForRefresh) return;
    if (refreshedMediaMentionsRef.current.has(mentionId)) return;
    refreshedMediaMentionsRef.current.add(mentionId);

    setMediaRefreshRequest((current) => ({
      mentionId,
      token: current?.mentionId === mentionId ? current.token + 1 : 1,
    }));
  }, [mentionId, mediaUrlForRefresh]);

  if (!mentionId) {
    return null;
  }

  if (!payload) {
    return null;
  }

  const hasNavigatorContext = currentIndex >= 0 && mentionIds.length > 1;
  const loadedCount = mentionIds.length;
  const hasNext = currentIndex >= 0 && (currentIndex + 1 < loadedCount || hasNextPage);
  const nav: PreviewMentionNavigator | undefined = hasNavigatorContext
    ? {
        currentIndex,
        total: total || loadedCount,
        hasPrev: currentIndex > 0,
        hasNext,
        isLoadingNext: isFetchingNextPage && pendingAdvanceRef.current !== null,
        onPrev: handlePrev,
        onNext: handleNext,
      }
    : undefined;

  return (
    <PreviewMentionModal
      isOpen={true}
      onClose={handleClose}
      mention={payload.mention}
      mediaUrl={payload.mediaUrl}
      mediaType={payload.mediaType}
      permalink={payload.permalink}
      hostedThumbnailUrl={payload.hostedThumbnailUrl}
      coverUrl={payload.coverUrl}
      nav={nav}
      onMediaError={handleMediaError}
    />
  );
};

export default GlobalMentionPreviewModal;
